import { createSlice } from '@reduxjs/toolkit';
import { request } from '../service/api/serviceRequest';
import { constructEndpoint } from '../utils/commonHelper';
import { LOADING_KEYS } from '../constants/loadingKeys';
import {
  SUCCESS_STATUS,
  GET_SSO_CLAIM_CONFIG,
  UPDATE_SSO_CLAIM_CONFIG,
  GET_SSO_SITE_CODES,
  CREATE_SSO_SITE_CODE,
  UPDATE_SSO_SITE_CODE,
  DELETE_SSO_SITE_CODE,
  GET_SSO_QUALIFIERS,
  CREATE_SSO_QUALIFIER,
  UPDATE_SSO_QUALIFIER,
  DELETE_SSO_QUALIFIER,
  GET_SSO_VALUE_MAPPINGS,
  CREATE_SSO_VALUE_MAPPING,
  UPDATE_SSO_VALUE_MAPPING,
  DELETE_SSO_VALUE_MAPPING,
  GET_SSO_LOGIN_RESOLUTIONS,
  GET_SSO_LOGIN_RESOLUTION,
  GET_SSO_OBSERVED_CLAIMS,
  SSO_RESOLVER_TEST,
  GET_USERS_PENDING_APPROVAL,
  APPROVE_PENDING_USER,
} from '../constants';

/**
 * State for the new SSO administration surface (claim pipeline, site codes, qualifiers,
 * access rules, migration, tester, resolutions, pending approvals).
 *
 * Kept as its own slice rather than folding into userSlice (2300+ lines already) per the
 * handover: this surface has an independent lifecycle from the legacy SSO CRUD screens.
 *
 * The backend for every one of these endpoints is being built in parallel and may not exist
 * yet, so every thunk here swallows network/HTTP errors and resets its slice to an empty
 * value instead of rethrowing, and skips the global error toast (skipToast) for the same
 * reason: a 404 during rollout is expected, not actionable by the admin looking at the screen.
 */
const initialState = {
  claimConfig: { data: null, status: 'idle' },
  siteCodes: { data: [], status: 'idle' },
  qualifiers: { data: [], status: 'idle' },
  accessRules: { data: [], status: 'idle' },
  resolutions: { items: [], total: 0, status: 'idle' },
  resolutionDetail: { data: null, status: 'idle' },
  observedClaims: { data: [], status: 'idle' },
  resolverTest: { request: null, result: null, status: 'idle' },
  pending: { items: [], status: 'idle' },
};

const ssoConfigSlice = createSlice({
  name: 'ssoConfig',
  initialState,
  reducers: {
    setClaimConfig(state, action) {
      state.claimConfig = { data: action.payload || null, status: 'succeeded' };
    },
    setSiteCodes(state, action) {
      state.siteCodes = { data: action.payload || [], status: 'succeeded' };
    },
    setQualifiers(state, action) {
      state.qualifiers = { data: action.payload || [], status: 'succeeded' };
    },
    setAccessRules(state, action) {
      state.accessRules = { data: action.payload || [], status: 'succeeded' };
    },
    setResolutions(state, action) {
      state.resolutions = {
        items: action.payload?.items || [],
        total: action.payload?.total || 0,
        distinctClaims: action.payload?.distinctClaims,
        status: 'succeeded',
      };
    },
    setResolutionDetail(state, action) {
      state.resolutionDetail = { data: action.payload || null, status: 'succeeded' };
    },
    setObservedClaims(state, action) {
      state.observedClaims = { data: action.payload || [], status: 'succeeded' };
    },
    setResolverTestRequest(state, action) {
      state.resolverTest = { request: action.payload, result: state.resolverTest.result, status: 'loading' };
    },
    setResolverTestResult(state, action) {
      state.resolverTest = { ...state.resolverTest, result: action.payload, status: 'succeeded' };
    },
    setPending(state, action) {
      state.pending = { items: action.payload?.items || [], status: 'succeeded' };
    },
    resetSsoConfig() {
      return initialState;
    },
  },
});

export const {
  setClaimConfig,
  setSiteCodes,
  setQualifiers,
  setAccessRules,
  setResolutions,
  setResolutionDetail,
  setObservedClaims,
  setResolverTestRequest,
  setResolverTestResult,
  setPending,
  resetSsoConfig,
} = ssoConfigSlice.actions;

export default ssoConfigSlice.reducer;

/* ---------------------------- Claim pipeline (domain detail) ---------------------------- */

export const getSsoClaimConfigAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_CLAIM_CONFIG, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_CLAIM_CONFIG,
      skipToast: true,
    });
    dispatch(setClaimConfig(data?.status === SUCCESS_STATUS ? data.data : null));
  } catch {
    dispatch(setClaimConfig(null));
  }
};

export const updateSsoClaimConfigAsync = (payload) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_CLAIM_CONFIG, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_CLAIM_CONFIG,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(setClaimConfig(data.data || payload));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ---------------------------------- Site codes ---------------------------------- */

export const getSsoSiteCodesAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_SITE_CODES, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
      skipToast: true,
    });
    dispatch(setSiteCodes(data?.status === SUCCESS_STATUS ? data.data?.siteCodes : []));
  } catch {
    dispatch(setSiteCodes([]));
  }
};

export const createSsoSiteCodeAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_SITE_CODE, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoSiteCodesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const updateSsoSiteCodeAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_SITE_CODE, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoSiteCodesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const deleteSsoSiteCodeAsync = (id, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(DELETE_SSO_SITE_CODE, id), {
      method: 'DELETE',
      loadingKey: LOADING_KEYS.SSO_SITE_CODES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoSiteCodesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ---------------------------------- Qualifiers ---------------------------------- */

export const getSsoQualifiersAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_QUALIFIERS, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
      skipToast: true,
    });
    dispatch(setQualifiers(data?.status === SUCCESS_STATUS ? data.data?.qualifiers : []));
  } catch {
    dispatch(setQualifiers([]));
  }
};

export const createSsoQualifierAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_QUALIFIER, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoQualifiersAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const updateSsoQualifierAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_QUALIFIER, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoQualifiersAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const deleteSsoQualifierAsync = (id, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(DELETE_SSO_QUALIFIER, id), {
      method: 'DELETE',
      loadingKey: LOADING_KEYS.SSO_QUALIFIERS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoQualifiersAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ------------------------------- Access rules (value mappings) ------------------------------- */

export const getSsoAccessRulesAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_VALUE_MAPPINGS, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
      skipToast: true,
    });
    dispatch(setAccessRules(data?.status === SUCCESS_STATUS ? data.data?.rules : []));
  } catch {
    dispatch(setAccessRules([]));
  }
};

export const createSsoAccessRuleAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(CREATE_SSO_VALUE_MAPPING, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoAccessRulesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const updateSsoAccessRuleAsync = (payload, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(UPDATE_SSO_VALUE_MAPPING, {
      method: 'PUT',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoAccessRulesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

export const deleteSsoAccessRuleAsync = (id, ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(DELETE_SSO_VALUE_MAPPING, id), {
      method: 'DELETE',
      loadingKey: LOADING_KEYS.SSO_ACCESS_RULES,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getSsoAccessRulesAsync(ssoDomainId));
    }
    return data;
  } catch (error) {
    return error;
  }
};

/* ------------------------------------- Login resolutions ------------------------------------- */

export const getSsoLoginResolutionsAsync = (filters) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_LOGIN_RESOLUTIONS, { ...filters }), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    dispatch(
      setResolutions(
        data?.status === SUCCESS_STATUS
          ? { items: data.data?.items, total: data.data?.total, distinctClaims: data.data?.distinctClaims }
          : { items: [], total: 0 }
      )
    );
  } catch {
    dispatch(setResolutions({ items: [], total: 0 }));
  }
};

export const getSsoLoginResolutionAsync = (id) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_LOGIN_RESOLUTION, id), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    dispatch(setResolutionDetail(data?.status === SUCCESS_STATUS ? data.data : null));
  } catch {
    dispatch(setResolutionDetail(null));
  }
};

export const getSsoObservedClaimsAsync = (ssoDomainId) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_SSO_OBSERVED_CLAIMS, ssoDomainId), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_LOGIN_RESOLUTIONS,
      skipToast: true,
    });
    dispatch(setObservedClaims(data?.status === SUCCESS_STATUS ? data.data?.claims : []));
  } catch {
    dispatch(setObservedClaims([]));
  }
};

/* ----------------------------------------- Tester ----------------------------------------- */

export const runSsoResolverTestAsync = (payload) => async (dispatch) => {
  dispatch(setResolverTestRequest(payload));
  try {
    const data = await request(SSO_RESOLVER_TEST, {
      method: 'POST',
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SSO_RESOLVER_TEST,
      skipToast: true,
    });
    dispatch(setResolverTestResult(data?.status === SUCCESS_STATUS ? data.data : null));
    return data;
  } catch (error) {
    dispatch(setResolverTestResult(null));
    return error;
  }
};

/* ------------------------------------- Pending approvals ------------------------------------- */

export const getUsersPendingApprovalAsync = (params) => async (dispatch) => {
  try {
    const data = await request(constructEndpoint(GET_USERS_PENDING_APPROVAL, { ...params }), {
      method: 'GET',
      loadingKey: LOADING_KEYS.SSO_PENDING_APPROVALS,
      skipToast: true,
    });
    dispatch(setPending(data?.status === SUCCESS_STATUS ? { items: data.data?.items } : { items: [] }));
  } catch {
    dispatch(setPending({ items: [] }));
  }
};

export const approvePendingUserAsync = (queueId, payload) => async (dispatch) => {
  try {
    const data = await request(APPROVE_PENDING_USER, {
      method: 'POST',
      body: JSON.stringify({ queueId, ...payload }),
      loadingKey: LOADING_KEYS.SSO_PENDING_APPROVALS,
    });
    if (data?.status === SUCCESS_STATUS) {
      dispatch(getUsersPendingApprovalAsync());
    }
    return data;
  } catch (error) {
    return error;
  }
};
